'use client';

import { useMemo } from 'react';
import RespImage from '@/app/_components/core/resp-image';
import { SmartVideo } from '@/app/_components/core/smart-video';
import {
  VIDEO_DESKTOP_WIDTH,
  VIDEO_DESKTOP_HEIGHT,
  VIDEO_MOBILE_WIDTH,
  VIDEO_MOBILE_HEIGHT,
  ORIENTATION_LANDSCAPE,
} from '@/app/_components/shared/hero-advanced/hero-advanced.constants';
import type { MediaLayerProps } from '@/app/_components/shared/hero-advanced/hero-advanced.types';
import { transformSrcsetCandidates } from '@/app/_lib/image-art-direction';
import { cn, emptyToUndefined } from '@/app/_lib/utils';
import { buildResponsiveVideoSizesFromFiles } from '@/app/_lib/video-utils';

// =============================================================================
// STYLE CONSTANTS
// =============================================================================

/**
 * Media container base styles with overlay support.
 * Overlay is rendered via ::after pseudo-element.
 */
const MEDIA_CONTAINER_BASE = [
  'relative',
  'h-full',
  'w-full',
  'overflow-hidden',
  // Overlay base (applied via ::after)
  'after:pointer-events-none',
  'after:absolute',
  'after:inset-0',
].join(' ');

/**
 * Dark overlay styles (overlay1)
 * - 45% black with multiply blend mode per Figma spec
 */
const OVERLAY_DARK = 'after:bg-overlay-dark after:mix-blend-multiply';

/**
 * Light overlay styles (default)
 */
const OVERLAY_LIGHT = 'after:bg-overlay-light';

/**
 * Fullscreen media (video/image) positioning
 */
const FULLSCREEN_MEDIA = 'absolute inset-0 h-full w-full object-cover';

// =============================================================================
// COMPONENT
// =============================================================================

export function MediaLayer({
  videoDesktop,
  videoMobile,
  backgroundImage,
  hasOverlay,
  priority,
}: MediaLayerProps) {
  // Memoize video sizes to prevent re-renders from causing video reload
  const videoSizes = useMemo(
    () =>
      buildResponsiveVideoSizesFromFiles(
        videoDesktop,
        videoMobile,
        { width: VIDEO_DESKTOP_WIDTH, height: VIDEO_DESKTOP_HEIGHT },
        { width: VIDEO_MOBILE_WIDTH, height: VIDEO_MOBILE_HEIGHT },
        ORIENTATION_LANDSCAPE
      ),
    [videoDesktop, videoMobile]
  );
  const hasVideo = videoSizes.length > 0;
  const autoPlay = videoDesktop?.[0]?.autoplay ?? videoMobile?.[0]?.autoplay ?? true;

  // Transform background image with art direction
  // Backend sends landscape/portrait keys - portrait is fallback, landscape gets media query
  const backgroundImageProps = backgroundImage
    ? (() => {
        const candidates = backgroundImage.srcsetCandidates;
        if (Array.isArray(candidates)) {
          return transformSrcsetCandidates(candidates);
        }
        // Record format with art direction
        return transformSrcsetCandidates({
          fallback: candidates.portrait ?? candidates.landscape ?? [],
          variants: candidates.landscape
            ? [{ media: ORIENTATION_LANDSCAPE, srcsetCandidates: candidates.landscape }]
            : [],
        });
      })()
    : null;

  return (
    <div
      className={cn(
        'relative col-start-1 row-start-1 size-full ',
        MEDIA_CONTAINER_BASE,
        hasOverlay ? OVERLAY_DARK : OVERLAY_LIGHT
      )}
      aria-hidden="true"
    >
      {hasVideo ? (
        <SmartVideo
          lazy={false}
          videoProps={{
            className: cn(FULLSCREEN_MEDIA, 'dark bg-background'),
            autoPlay,
            muted: autoPlay,
            loop: true,
            playsInline: true,
            controls: !autoPlay,
          }}
          pictureProps={{
            className: 'absolute inset-0 h-full w-full',
          }}
          imgProps={{
            alt: '',
            className: 'h-full w-full object-cover',
          }}
          sizes={videoSizes}
        />
      ) : backgroundImageProps ? (
        <RespImage
          srcsetCandidates={backgroundImageProps.srcsetCandidates}
          artDirection={backgroundImageProps.artDirection}
          alt={backgroundImage?.altText ?? ''}
          title={emptyToUndefined(backgroundImage?.title)}
          sizes="(min-width: 1920px) 1920px, 100vw"
          className={cn(FULLSCREEN_MEDIA, 'dark bg-background')}
          priority={priority}
        />
      ) : null}
    </div>
  );
}